import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessagesInit } from 'hooks/use-iq-chat-messages/use-iq-chat-messages.types';
import { ChatContextInit, ChatContextValue, extractMax4Collections } from 'store/iq/chat-context.slice';
import { uniqueId } from 'utils/unique-id/unique-id';
import { ChatState, ChatStoreInit } from './chat-store.types';

export const gptSettings = {
  model: 'gpt-4o-2024-05-13',
  temperature: 0,
  max_tokens: 1024,
  stream: true,
  seed: 1,
};

const firstUserMessage = {
  value: [],
  state: {
    firstUserMessage:
      'Your minimum task is to CREATE a COLLECTION; additionally if the prompt mentions it, you will insert data. Consider the below Information to determine whether this task can be accomplished:\n\nInformation: \n\n- Prompt: "Create a sample collection with sample data"\n\n- Bucket: `test`\n\n- Scope: `test`\n\n- Taken collection names: `example_collection`, `sample_collection`, `sample_collection1`, `sample_data`, `sample_dataset`, `sample_entries`, `sample_items`, `sample_records` \n\nHaving considered the above Information, follow these steps:\n\n1. Ask yourself: is the user prompt relevant to the task at hand? If not, first write "#ERR:" and politely explain why not.\n\n2. If the user asks for sample data/collections, be creative in your naming, based on the bucket and scope name and existing collection names.\n\n3. If the task can\'t be accomplished, say "#ERR:" and politely explain why not.\n\n4. Output valid SQL++ only and with no explanation.',
    upcomingAssistantMsgIdx: 1,
  },
} as ChatMessagesInit;

// this initial state doesn't generate a new uuid, so its only used for the initial slice state
const initialChatStore: ChatStoreInit = {
  state: {
    isLoading: false,
    isResponding: false,
    query: '',
  },
  messages: firstUserMessage,
  context: {
    value: {},
  },
  debug: {},
  chatId: uniqueId(),
};

// this initial state DOES generate new uuids, so this should be used for creating new (empty) chat stores
export const createInitialChatStore = (currentContextInit?: ChatContextInit): ChatStoreInit => {
  return {
    state: {
      isLoading: false,
      isResponding: false,
      query: '',
    },
    messages: firstUserMessage,
    context: currentContextInit ?? {
      value: {},
    },
    debug: {},
    chatId: uniqueId(),
  };
};

export const chatStoreSliceName = 'chatStore';

export const chatStoreSlice = createSlice({
  name: chatStoreSliceName,
  initialState: initialChatStore,
  reducers: {
    initializeChatStore: (state, action: PayloadAction<ChatStoreInit>) => {
      state.chatId = action.payload.chatId;
      state.messages = action.payload.messages;
      state.context = action.payload.context;
      state.debug = action.payload.debug;
      state.state = action.payload.state;
    },
    resetChatStore: (state, action: PayloadAction<Partial<ChatStoreInit>>) => {
      state.state.isLoading = action.payload.state?.isLoading || false;
      state.state.isResponding = action.payload.state?.isResponding || false;
      state.state.query = action.payload.state?.query || '';
      state.messages = action.payload.messages ?? firstUserMessage;
      state.context.value = {
        bucket: action.payload.context?.value?.bucket || '',
        scope: action.payload.context?.value?.scope || '',
        collections: action.payload.context?.value?.collections || [],
      };
      state.debug = {};
    },
    setLoading: (state, action) => {
      state.state.isLoading = action.payload;
    },
    setResponding: (state, action) => {
      state.state.isResponding = action.payload;
    },
    setQuery: (state, action) => {
      state.state.query = action.payload;
    },
    updateChatState: (state, action: PayloadAction<Partial<ChatState>>) => {
      state.state = { ...state.state, ...action.payload };
    },
    updateMessagesAndIdx: (state, action) => {
      if (state.messages && state.messages.state) {
        state.messages.value = action.payload.value;
        state.messages.state.upcomingAssistantMsgIdx = action.payload.index;
      }
    },
    setFirstUserMessage: (state, action: PayloadAction<string>) => {
      if (state.messages && state.messages.state) {
        state.messages.state.firstUserMessage = action.payload;
      }
    },
    setUpcomingAssistantMsgIdx: (state, action: PayloadAction<number>) => {
      if (state.messages?.state) {
        state.messages.state.upcomingAssistantMsgIdx = action.payload;
      }
    },
    updateChatContextForChat: (state, action: PayloadAction<Partial<ChatContextValue>>) => {
      const { bucket, scope, collections } = action.payload;
      if (state.context.value) {
        if (bucket) {
          state.context.value.bucket = bucket;
        }
        if (scope || scope === '') {
          state.context.value.scope = scope;
        }
        if (collections) {
          state.context.value.collections = extractMax4Collections(collections);
        }
      }
    },
  },
});

export const {
  initializeChatStore,
  setQuery,
  setLoading,
  setFirstUserMessage,
  setResponding,
  resetChatStore,
  updateMessagesAndIdx,
  setUpcomingAssistantMsgIdx,
  updateChatState,
  updateChatContextForChat,
} = chatStoreSlice.actions;
export default chatStoreSlice.reducer;
